import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setCooldownEnd, selectCooldownEnd } from "features/resendCooldownSlice";

interface ResendCooldownButtonProps {
    onResend: () => void;
    cooldownPeriodMs?: number; // default 2 minutes
    buttonClassName?: string;
    disabledStyle?: React.CSSProperties;
    activeStyle?: React.CSSProperties;
    children?: React.ReactNode;
}

export const ResendCooldownButton: React.FC<ResendCooldownButtonProps> = ({
    onResend,
    cooldownPeriodMs = 2 * 60 * 1000,
    buttonClassName = "link",
    disabledStyle = { pointerEvents: "none", opacity: 0.5 },
    activeStyle = { opacity: 1 },
    children = "Resend Code",
}) => {
    const dispatch = useDispatch();
    const cooldownEnd: number | null = useSelector(selectCooldownEnd);
    const [remaining, setRemaining] = useState(0);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (cooldownEnd) {
            const update = () => {
                const now = Date.now();
                const diff = Math.max(0, Math.floor((cooldownEnd - now) / 1000));
                setRemaining(diff);
                if (diff <= 0 && timerRef.current) {
                    clearInterval(timerRef.current);
                    timerRef.current = null;
                }
            };
            update();
            if (!timerRef.current) {
                timerRef.current = setInterval(update, 1000);
            }
        } else {
            setRemaining(0);
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        }
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [cooldownEnd]);

    const handleClick = () => {
        if (remaining > 0) return;
        const end = Date.now() + cooldownPeriodMs;
        dispatch(setCooldownEnd(end));
        onResend();
    };

    const formattedRemaining = `${String(Math.floor(remaining / 60)).padStart(2, "0")}:${String(remaining % 60).padStart(2, "0")}`;

    return (
        <a
            className={buttonClassName}
            onClick={handleClick}
            style={remaining > 0 ? disabledStyle : activeStyle}
        >
            {remaining > 0 ? (
                <>{children} ({formattedRemaining})</>
            ) : (
                children
            )}
        </a>
    );
};
